import type { ApiExecutorHttpMethod } from '../models/external-api-docs.model';

export const STORAGE_KEYS = {
  JWT_TOKEN: 'jwtToken',
  TOKEN_INFO: 'tokenInfo',
} as const;

export const ErrorHandlerConstants = {
  Error_400: 'Element Not Found Please Provide Valid Trade Id',
  Error_500:
    'Sorry, Operation could not be completed at this time.\n Back-end returned with an Internal Server Error. Please contact customer support',
  Default_Error:
    'Sorry, Operation could not be completed at this time.\n Please contact customer support',
  Initial_Error_Text: 'Sorry, Operation could not be completed. \n Error: \n',
  String_Declare: 'string',
  Connective_Error:
    'Sorry, we are having connectivity issues, Please contact our Pulse customer engagement for more help',
  Error_502:
    'Sorry, Operation could not be completed at this time.\n Back-end returned with an Timeout Error. Please contact customer support',
  SessionTimout: 'Session timedout, Please login again',
  InvalidPackageType: 'Please enter a valid packaging type',
};

export const SnackBar = {
  Close: 'Close',
};

export const HttpSignatures: HttpSignature = {
  Post: 'POST',
  Put: 'PUT',
  Delete: 'DELETE',
  Get: 'GET',
};

export interface HttpSignature {
  [name: string]:
    | 'DELETE'
    | 'GET'
    | 'HEAD'
    | 'JSONP'
    | 'OPTIONS'
    | 'POST'
    | 'PUT';
}

export enum StatusCodes {
  Success = 200,
  SuccessfulRequest = 204,
  BadRequest = 400,
  Unauthorised = 401,
  Forbidden = 403,
  NotFound = 404,
  InternalServerError = 500,
}

export const BreadcrumbNames = {
  home: 'Home',
  contactsales: 'Contact Sales',
  shipmentTracking: 'Track Shipments',
  manifestTracking: 'Track Manifests',
  manifestUpload: 'IBC Manifest Upload',
  genAirWaybill: 'IBC Air Waybill System',
  shipmentInfo: 'Shipment Tracker Information',
  airway: 'Air WayBill',
  addressBook: 'Address Book',
  historyTab: 'History Tab',
  aams: 'AAMS-Documents',
  apiDocumentation: 'API Documentation',
};

export const apiDocumentationDocBreadcrumbs: Record<string, string> = {
  'label-services': 'Label Services',
  'air-ams-manifest': 'Air AMS Manifest',
  'ibc-track': 'IBC Track',
  'web-airbill': 'Web Airbill',
};

export const externalApiDocsMessages = {
  defaultLoadError: 'Documentation could not be loaded.',
  requestFailed:
    'Unable to load API documentation. Please try again later.',
  msalSignInCancelled: 'Sign-in was cancelled.',
  msalSignInFailedDefault: 'Sign-in failed. Please try again.',
} as const;

export const externalApiDocsTokenMask = {
  character: '*',
  shortTokenMaxLength: 16,
  prefixLength: 8,
  suffixLength: 6,
  middle: ' … ',
} as const;

export const externalApiDocsTokenRevealUi = {
  hideToken: 'Hide token',
  showToken: 'Show token',
  visibilityOnIcon: 'visibility_on',
  visibilityOffIcon: 'visibility_off',
  copyTokenAriaLabel: 'Copy token to clipboard',
  copyTokenTooltip: 'Copy to clipboard',
  copyIcon: 'copy',
} as const;

export const externalApiDocKeys = {
  labelService: 'labelService',
  airAmsManifest: 'airAmsManifest',
  webAirbill: 'webAirbill',
} as const;

export const apiExecutorTabs = {
  request: 'request',
  response: 'response',
  curl: 'curl',
} as const;

export type ApiExecutorTabId =
  (typeof apiExecutorTabs)[keyof typeof apiExecutorTabs];

export const apiExecutorCopyKeys = {
  url: 'url',
  curl: 'curl',
} as const;

export type ApiExecutorCopyKey =
  (typeof apiExecutorCopyKeys)[keyof typeof apiExecutorCopyKeys];

export const DashboardActionButtonNames = {
  accessAamsDoc: 'ACCESS AAMS DOCUMENTS',
  trackManifest: 'TRACK MANIFESTS',
  shipmentTracking: 'TRACK SHIPMENTS',
  contactSales: 'CONTACT SALES',
  manifestUpload: 'UPLOAD MANIFEST',
  genAirWaybill: 'GENERATE AIR WAYBILL',
  aamsDocuments: 'ACCESS AAMS DOCUMENTS',
  apiDocumentation: 'GO TO API DOCS',
};

export const ModalPopupMessages = {
  success: 'success',
  error: 'error',
  Warning: 'Warning',
};

export const MapPreviewMessages = {
  incomplete: 'Select country, state, and city before viewing the map.',
  cityNotRecognized: 'City is not valid for the selected country.',
  stateNotRecognized: 'State is not valid for the selected country.',
  unavailable:
    'Map preview is not available. Contact support if this continues.',
};

export const pageUrls = {
  shipmentTrackerInfoPage: '/shipment-tracking/shipment-information',
  shipmentTrackerInfoFromHistoryPage:
    '/air-waybill/history-tab/shipment-information',
  shipmentTrackFormPage: '/shipment-tracking',
  trackManifest: '/track-manifest',
  manifestUpload: '/manifest-upload',
  genAirWaybill: '/air-waybill',
  aamsDocuments: 'aams-documents',
  apiDocumentation: '/api-documentation',
};

export const bannerMessages = {
  shipmentTrack: `To track your shipment through IBC's worldwide network, simply enter the tracking number you wish to track and click on the "Track shipment" button. You will receive detailed information about the status of your shipment in moments.`,
  aamsDocWarningMessage:
    "Please review the sales team's SOP documentation for contact details and for the specific operational procedures.",
};

export const popupWidth = {
  gatewayPopup: '95vw',
  warningPopup: '340px',
  searchPopup: '500px',
  addressVerifyPopup: '600px',
  commericalInvoicePopup: '70vw',
  windowAlert:
    'Password has been reset successfully. \n Please sign-in with your new password',
};

/**
 * Customer shipment-tracker event labels.
 * Aligned with IBC EventsService.handleEventType for hawb/shipment context.
 * Lookup order in resolveShipmentTrackerEventDescription:
 *   1) eventType_action_name  2) eventType  3) type-level fallbacks
 */
export const EVENT_DESCRIPTIONS: { [key: string]: string } = {
  // Gateway / publish / block
  g: 'Gatewayed',
  pb: 'Manifest Published',
  pb_c_hawb: 'Manifest Published',
  pb_c_manifest: 'Manifest Published',
  pb_c_mawb: 'MAWB Published',
  bk: 'Blocked',
  bk_u_hawb: 'Blocked',
  ubk: 'Unblocked',
  ubk_u_hawb: 'Unblocked',

  // Parent object create/update (shipment)
  po: 'Updated',
  po_c_hawb: 'Created',
  po_u_hawb: 'Updated',
  po_c_bag: 'Bag added',
  po_u_bag: 'Bag updated',
  po_c_manifest: 'Manifest added',
  po_u_manifest: 'Manifest updated',
  po_c_mawb: 'Mawb added',
  po_u_mawb: 'Mawb updated',
  cs: 'Created',
  cs_c_hawb: 'Created',

  // Link / delink — hawb tracker perspective (matches IBC isFrom=hawb)
  l: 'Linked',
  l_a_bag: 'Shipment Bagged',
  l_a_manifest: 'Shipment Manifested',
  l_a_mawb: 'Shipment Added to MAWB',
  e: 'Removed',
  e_r_bag: 'Shipment Unbagged',
  e_r_manifest: 'Shipment removed from Manifest',
  e_r_mawb: 'Shipment removed from MAWB',

  // Commodity
  cm: 'Commodity Updated',
  cm_c_hawb: 'Commodity Added',
  cm_a_hawb: 'Commodity Added',
  cm_u_hawb: 'Commodity Updated',
  cm_d_hawb: 'Commodity Deleted',
  cm_r_hawb: 'Commodity Removed',

  // Flight
  f: 'Flight Updated',
  f_c_hawb: 'Flight Added',
  f_a_hawb: 'Flight Added',
  f_u_hawb: 'Flight Updated',
  f_d_hawb: 'Flight Removed',
  f_r_hawb: 'Flight Removed',

  // Notes
  in: 'Note added',
  in_c_hawb: 'Note added',
  cn: 'Note added',
  cn_c_hawb: 'Note added',
  cin: 'Note added',
  ccn: 'Note added',
  fin: 'Note added',
  fcn: 'Note added',

  // Attachments / documents
  at: 'Document Uploaded',
  at_c_hawb: 'Document Uploaded',
  at_a_hawb: 'Document Uploaded',
  at_d_hawb: 'Document Deleted',
  cat: 'Document Uploaded',
  fat: 'Document Uploaded',

  // Roles
  hr: 'Role Added',
  hr_c_hawb: 'Role Added',
  hr_a_hawb: 'Role Added',
  cr: 'Role Added',
  cr_c_hawb: 'Role Added',
  cr_a_hawb: 'Role Added',

  // Status codes (Node: sc; SP may return st / pd)
  sc: 'Status Code Updated',
  sc_c_hawb: 'Status Code Added',
  sc_u_hawb: 'Status Code Updated',
  st: 'Status Code Updated',
  st_c_hawb: 'Status Code Added',
  st_u_hawb: 'Status Code Updated',
  pd: 'Status Code Updated',
  pd_c_hawb: 'Status Code Added',
  pd_u_hawb: 'Status Code Updated',

  // CBP / clearance
  cbp: 'Shipment Held',

  // Auto-rate / charges (may appear on shipment history)
  ar: 'Shipment Auto Rated',
  apc: 'AP Charge Updated',
  apc_c_hawb: 'AP Charge Added',
  apc_a_hawb: 'AP Charge Added',
  apc_u_hawb: 'AP Charge Updated',
  apc_d_hawb: 'AP Charge Deleted',
  arc: 'AR Charge Updated',
  arc_c_hawb: 'AR Charge Added',
  arc_a_hawb: 'AR Charge Added',
  arc_u_hawb: 'AR Charge Updated',
  arc_d_hawb: 'AR Charge Deleted',
};

export type ShipmentTrackerEventLike =
  | string
  | {
      eventType?: string;
      action?: string;
      eventAction?: string;
      name?: string;
      formatedDateTime?: string;
      date?: string;
      time?: string;
      info?: { data?: { clearedOrHeldBy?: string; actionCode?: string } };
    }
  | null
  | undefined;

/**
 * Resolve customer-facing event label for shipment tracker.
 * Used by Update History, Shipment Status, and Pieces status.
 * Mirrors IBC EventsService.handleEventType for hawb context.
 */
export function resolveShipmentTrackerEventDescription(
  item: ShipmentTrackerEventLike,
): string {
  // Already-resolved label (e.g. after prior bind / localStorage) — keep it
  if (typeof item === 'string') {
    const trimmed = item.trim();
    if (!trimmed || trimmed === 'Unknown Event') {
      return '';
    }
    return trimmed;
  }

  if (!item || typeof item !== 'object') {
    return '';
  }

  if (!item.eventType) {
    return '';
  }

  const eventType = String(item.eventType).trim();
  const action = String(item.eventAction ?? item.action ?? '').trim();
  const name = String(item.name ?? '').trim();
  const compositeKey = `${eventType}_${action}_${name}`;

  if (EVENT_DESCRIPTIONS[compositeKey]) {
    return EVENT_DESCRIPTIONS[compositeKey];
  }
  if (EVENT_DESCRIPTIONS[eventType]) {
    // Refine status-code create vs update when only type-level key exists
    if (
      (eventType === 'sc' || eventType === 'st' || eventType === 'pd') &&
      (action === 'c' || action === 'a')
    ) {
      return 'Status Code Added';
    }
    return EVENT_DESCRIPTIONS[eventType];
  }

  // CBP: prefer clearedOrHeldBy text when present (IBC behavior)
  if (eventType === 'cbp') {
    const clearedOrHeldBy = item.info?.data?.clearedOrHeldBy;
    if (typeof clearedOrHeldBy === 'string' && clearedOrHeldBy.trim()) {
      return clearedOrHeldBy.trim();
    }
    return item.info?.data?.actionCode === 'CC'
      ? 'Shipment Cleared'
      : 'Shipment Held';
  }

  // Link / delink by linked object name (hawb tracker perspective)
  if (eventType === 'l') {
    if (name === 'bag') return 'Shipment Bagged';
    if (name === 'manifest') return 'Shipment Manifested';
    if (name === 'mawb') return 'Shipment Added to MAWB';
    return 'Linked';
  }
  if (eventType === 'e') {
    if (name === 'bag') return 'Shipment Unbagged';
    if (name === 'manifest') return 'Shipment removed from Manifest';
    if (name === 'mawb') return 'Shipment removed from MAWB';
    return 'Removed';
  }

  // Commodity / flight by action
  if (eventType === 'cm' || eventType === 'f') {
    const label = eventType === 'cm' ? 'Commodity' : 'Flight';
    if (action === 'c' || action === 'a') return `${label} Added`;
    if (action === 'u') return `${label} Updated`;
    if (action === 'd' || action === 'r') return `${label} Removed`;
    return `${label} Updated`;
  }

  // Attachments
  if (eventType === 'at' || eventType === 'cat' || eventType === 'fat') {
    return action === 'd' ? 'Document Deleted' : 'Document Uploaded';
  }

  // Notes / roles
  if (['in', 'cn', 'cin', 'ccn', 'fin', 'fcn'].includes(eventType)) {
    return 'Note added';
  }
  if (eventType === 'hr' || eventType === 'cr') {
    return 'Role Added';
  }

  // Parent / create shipment
  if (eventType === 'po' || eventType === 'cs') {
    return action === 'c' || action === 'a' ? 'Created' : 'Updated';
  }

  // Last resort: never show "Unknown Event" — use action-based label
  if (action === 'c' || action === 'a') return 'Created';
  if (action === 'u') return 'Updated';
  if (action === 'd' || action === 'r') return 'Removed';
  return 'Updated';
}

export const specialKeys = [
  'Backspace',
  'Tab',
  'End',
  'Home',
  'ArrowLeft',
  'ArrowRight',
  'Control',
];

export const ErrorMessages = {
  noMawb: 'No event information found for the entered MAWB',
  noManifest:
    'No Manifest, Bags, or Shipments are associated with the entered MAWB',
  noShipment: 'No Bags, or Shipments are associated with the entered MAWB',
  noData: 'No Data',
  noHistoryData: 'Please perform a search to view all the Air Waybills',
  sessionExpiredMessage:
    'Your session has expired. Please log in again to continue.',
};

export const allowedKeys = [
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Shift',
  'Control',
  'Alt',
  'Escape',
];

export const countryCodes = {
  usa: 'US',
  canada: 'CA',
  brazil: 'BR',
  unitedKingdom: 'GB',
};

export const stateRequiredCountryCodes: readonly string[] = [
  countryCodes.usa,
  countryCodes.canada,
  countryCodes.brazil,
];

export const consigneePostalRequiredCountryCodes: readonly string[] = [
  ...stateRequiredCountryCodes,
  countryCodes.unitedKingdom,
];

export function isStateRequiredCountry(countryCode: string): boolean {
  return stateRequiredCountryCodes.includes(countryCode);
}

export function isConsigneePostalRequiredCountry(countryCode: string): boolean {
  return consigneePostalRequiredCountryCodes.includes(countryCode);
}

export function isConsigneePostalOnlyCountry(countryCode: string): boolean {
  return (
    isConsigneePostalRequiredCountry(countryCode) &&
    !isStateRequiredCountry(countryCode)
  );
}

export const validationErrorMessages = {
  autocompleteError: 'Invalid value entered',
  invalidPostalCode: 'Invalid Postal Code',
  invalidEmail: 'Invalid Email',
  invalidStateOrCity: 'Please select Country first',
  MandatoryFieldsIntro:
    'Please complete all the required fields in the following sections:',
  MandatoryFieldsFooter: 'Check all inputs before proceeding.',
  CommercialInvoiceValidation:
    'Please generate the air waybill to proceed with the commercial invoice',
  Validation: 'Validation',
  phoneMinLengthDigits: 'Phone must have 4 to 16 digits',
  phoneCountryMismatch:
    "The number of digits you entered doesn't match with the country selected. Would you like to proceed further?",
  addressBookSavePrompt:
    'Would you like to save the address(es) in the Address book?',
  countryChangeTitle: 'Change Country',
  countryChangeClearsAddress:
    'Changing the country will clear the address, state, city and zip code. Do you want to continue?',
};

export const LookupColumnsConfig = [
  {
    label: 'Country',
    key: 'country',
  },
  {
    label: 'State',
    key: 'state',
  },
  {
    label: 'City',
    key: 'city',
  },
];

export const lookupHeaders = {
  country: 'Country',
  state: 'State',
  city: 'City',
};

export const lookupPluralHeaders = {
  countries: 'Countries',
  states: 'States',
  cities: 'Cities',
};

export const gridMessages = {
  noRecordsMatch: (itemName: string) => `No ${itemName} matched your search`,
};

export const MasterLookup = {
  countries: 'countries',
  serviceTypes: 'serviceType',
  deliveryTerms: 'delivery_terms',
  shipmentContentType: 'shipment_content_type',
};

export const apiActions = {
  c: 'c',
  create: 'create',
  edit: 'edit',
  delete: 'delete',
};

export const airwayBillConstantNames = {
  pieceAdd: 'Add a Piece',
  pieceUpdate: 'Update a Piece',
  commodityAdd: 'Add a Commodity',
  commodityUpdate: 'Update a Commodity',
  shipperType: 'S',
  consigneeType: 'C',
  shipmentTypeApx: 'APX',
  shipmentTypeDoc: 'DOC',
  downloadInvoice: 'Download invoice',
  downloadAirwayBill: 'Download AWB',
};

export const airwayBillType = ['Dutiable Shipments', 'Document'];

export const weightMetricList = ['Pounds', 'Kilograms'];

export const sizeMetricList = ['Inches', 'Centimeters'];

export const measurementOptions = ['Pounds/Inches', 'Kilograms/Centimeters'];

export const PHONE_FORMAT_CONSTANTS = {
  CONTROL_KEYS: ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'],
  VALIDATION_LIMITS: {
    DIGITS_MIN: 4,
    DIGITS_MAX: 16,
  },
  ERROR_TYPES: {
    PHONE_MIN_LENGTH: 'phoneMinLength',
    PHONE_MAX_LENGTH: 'phoneMaxLength',
  },
  REVALIDATION_DELAY: 10,
};

export const PHONE_DIGIT_LIMITS = {
  MIN: 4,
  MAX: 16,
};

export const PHONE_VALIDATION_RESULTS = {
  TOO_SHORT: 'TOO_SHORT',
  TOO_LONG: 'TOO_LONG',
  INVALID_LENGTH: 'INVALID_LENGTH',
  NOT_A_NUMBER: 'NOT_A_NUMBER',
  INVALID_COUNTRY: 'INVALID_COUNTRY',
} as const;

export type PhoneValidationResult =
  typeof PHONE_VALIDATION_RESULTS[keyof typeof PHONE_VALIDATION_RESULTS];

export const DIALOG_ACTIONS = {
  CONTINUE: 'continue',
  YES: 'yes',
  NO: 'no',
} as const;

export const MESSAGE_POPUP_SOURCE = {
  PHONE_COUNTRY_MISMATCH: 'phone-country-mismatch',
  ADDRESS_BOOK_SAVE_PROMPT: 'address-book-save-prompt',
  COUNTRY_CHANGE_CONFIRM: 'country-change-confirm',
} as const;

/** Address fields disabled until a country is selected and cleared when it changes. */
export const COUNTRY_DEPENDENT_FIELDS = [
  'address_1',
  'address_2',
  'state',
  'city',
  'zipCode',
] as const;

export const AIRWAY_BILL_PRINT_MESSAGE = {
  READY: 'AIRWAY_BILL_PRINT_READY',
  PING: 'AIRWAY_BILL_PRINT_PING',
} as const;

export const PHONE_REGEX_PATTERNS = {
  ALLOWED_CHARACTERS: /[a-zA-Z0-9]/,
  EXTRACT_DIGITS: /\D/g,
  EXTRACT_ALPHANUMERIC: /[^a-zA-Z0-9]/g,
  REMOVE_INVALID_CHARS: /[^a-zA-Z0-9]/g,
  FORMATTING_CHARS: /[ ()-]/g,
};


export const JSON_PLAIN_INTEGER_STRING_PATTERN = /^-?\d+$/;

export const API_EXECUTOR_NORMALIZE = {
  DEFAULT_TITLE: 'API request',
  DEFAULT_METHOD: 'POST',
  EMPTY: '',
  DEFAULT_HEADER_TYPE: 'string',
  HEADER_NAMES: {
    CONTENT_TYPE: 'Content-Type',
    AUTHORIZATION: 'Authorization',
  },
  HEADER_EXAMPLES: {
    APPLICATION_JSON: 'application/json',
    BEARER_TOKEN: '',
  },
  HEADER_NAMES_LOWER: {
    CONTENT_TYPE: 'content-type',
    AUTHORIZATION: 'authorization',
  },
  KEYS: {
    name: 'name',
    type: 'type',
    example: 'example',
    required: 'required',
    editable: 'editable',
    title: 'title',
    description: 'description',
    method: 'method',
    url: 'url',
    headers: 'headers',
    payload: 'payload',
    response: 'response',
    curl: 'curl',
    code: 'code',
    message: 'message',
    href: 'href',
    is_error: 'is_error',
    completion_time: 'completion_time',
  },
  TYPEOF: {
    STRING: 'string',
    OBJECT: 'object',
    NUMBER: 'number',
    BOOLEAN: 'boolean',
    BIGINT: 'bigint',
  },
} as const;

export const API_EXECUTOR_HTTP_METHODS: readonly ApiExecutorHttpMethod[] = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
];

export const API_EXECUTOR_METHODS_WITH_BODY: readonly ApiExecutorHttpMethod[] = [
  'POST',
  'PUT',
  'PATCH',
];

export const API_EXEC_JSON_HIGHLIGHT = {
  classNames: {
    key: 'api-exec__json-key',
    str: 'api-exec__json-str',
    num: 'api-exec__json-num',
    bool: 'api-exec__json-bool',
    punct: 'api-exec__json-punct',
  },
  patterns: {
    key: /(&quot;)([\w-]+)(&quot;):/g,
    str: /: (&quot;)([^&]*?)(&quot;)(\s{0,64}(?:[,\n]|$))/g,
    num: /: (-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)([ \t]{0,64}(?:,|\n|$))/g,
    bool: /: (true|false|null)([ \t]{0,64}(?:,|\n|$))/g,
    punct: /([{}[\]])/g,
  },
} as const;

export const CITY_MIN_SEARCH_LENGTH = 3;

export const LookupDisplaySeparator = ' - ' as const;

export const AirWayBillStringPatterns = {
  WhiteSpaceGlobal: /\s/g,
} as const;

export const AddressVerificationMessages = {
  invalid:
    "We couldn't verify this address. Please check the street, city, state and postal code.",
  secondaryIssue:
    'The street address is valid, but the apartment / suite / unit number is missing or not recognised.',
  ambiguous: 'We found more than one possible match.',
  correctedNotOverridable:
    'The address was corrected during verification. Please use the suggested address or edit the address.',
  correctedOverridable:
    'The address could only be partly confirmed, or was changed during verification. You can use the suggested address or keep what you entered.',
} as const;

export const AddressVerificationTitles = {
  address: 'Verify Address',
  shipper: 'Verify Shipper Address',
  consignee: 'Verify Consignee Address',
} as const;

/** Backend error code for a save blocked by address verification (details.verification). */
export const ADDRESS_VERIFICATION_REJECTION_CODE = 'BUSINESS_RULE_VIOLATED';

export const SmartyApiUrls = {
  usAutocomplete: 'https://us-autocomplete-pro.api.smarty.com/lookup',
  internationalAutocomplete:
    'https://international-autocomplete.api.smarty.com/v2/lookup',
} as const;

export const SMARTY_AUTOCOMPLETE = {
  MIN_SEARCH_LENGTH: 3,
  DEBOUNCE_MS: 300,
  MAX_RESULTS: 10,
} as const;

/** Country codes routed to Smarty's US APIs; everything else uses the international APIs. */
export const SMARTY_US_COUNTRY_CODES: readonly string[] = ['US', 'USA'];